import React, { useState, useEffect, useCallback } from 'react';
import { bookings } from '../../api';
import './Bookings.css';

const SESSION_STATUS_META = {
  scheduled: { label: 'Upcoming', color: '#3498db', bg: '#eaf4fb' },
  pending: { label: 'Upcoming', color: '#3498db', bg: '#eaf4fb' },
  completed: { label: 'Completed', color: '#27ae60', bg: '#e9f7ef' },
  'no-show': { label: 'No-show', color: '#e67e22', bg: '#fdf1e6' },
  canceled: { label: 'Canceled', color: '#95a5a6', bg: '#f2f3f4' }
};

function fmtDateTime(d) {
  if (!d) return '⏳ not scheduled';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Read-only - a session gets its time the moment the coach approves any
// regular lesson booking with them while this pack is active (it
// auto-fills the next open session), or the coach sets it manually. The
// player never picks a time here; see MyCoachingPacks' header hint.
function SessionRow({ session }) {
  const meta = SESSION_STATUS_META[session.status] || SESSION_STATUS_META.scheduled;

  return (
    <div style={{ padding: '6px 0', borderBottom: '1px solid #eef1f4', fontSize: '12px', color: '#2c3e50' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'bold', color: '#7f8c8d', minWidth: '26px' }}>#{session.sequenceNumber}</span>
        <span style={{
          minWidth: '150px',
          color: session.scheduledDate ? '#2c3e50' : '#e67e22',
          fontWeight: session.scheduledDate ? 'normal' : 'bold'
        }}>
          {fmtDateTime(session.scheduledDate)}
        </span>
        <span style={{
          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
          color: meta.color, background: meta.bg
        }}>
          {meta.label}
        </span>
      </div>
    </div>
  );
}

function PackCard({ plan }) {
  const [open, setOpen] = useState(false);

  const p = plan.progress || { total: plan.sessionCount, used: 0, remaining: plan.sessionCount, canceled: 0, unscheduled: 0, isComplete: false, nextSession: null };
  const coachName = plan.coachId && typeof plan.coachId === 'object' ? plan.coachId.name : null;
  const pct = p.total ? Math.round((p.used / p.total) * 100) : 0;
  const low = !p.isComplete && p.remaining > 0 && p.remaining <= 2;
  const unscheduled = p.unscheduled || 0;

  return (
    <div style={{
      border: `2px solid ${p.isComplete ? '#27ae60' : '#3498db'}`,
      borderRadius: '10px',
      padding: '14px',
      marginBottom: '12px',
      background: 'white'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '10px' }}>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#2c3e50' }}>{plan.name}</div>
          {coachName && <div style={{ fontSize: '12px', color: '#7f8c8d' }}>👨‍🏫 {coachName}</div>}
        </div>
        <span style={{
          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold',
          color: '#fff', background: p.isComplete ? '#27ae60' : '#3498db', whiteSpace: 'nowrap'
        }}>
          {p.isComplete ? '✅ Complete' : '🔄 Active'}
        </span>
      </div>

      <div style={{ marginTop: '10px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', fontSize: '12px',
          fontWeight: 'bold', color: '#2c3e50', marginBottom: '4px'
        }}>
          <span>{p.used} / {p.total} sessions used</span>
          <span style={{ color: low ? '#e67e22' : '#7f8c8d' }}>{p.remaining} left</span>
        </div>
        <div style={{ height: '8px', background: '#ecf0f1', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: p.isComplete ? '#27ae60' : '#3498db', transition: 'width 0.3s ease' }} />
        </div>
        {p.nextSession && !p.isComplete && (
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '6px' }}>
            ⏭️ Next session: {fmtDateTime(p.nextSession.scheduledDate)}
          </div>
        )}
        {unscheduled > 0 && !p.isComplete && (
          <div style={{ fontSize: '12px', color: '#e67e22', fontWeight: 'bold', marginTop: '6px' }}>
            📅 {unscheduled} session{unscheduled !== 1 ? 's' : ''} still need a time — book any available lesson with {coachName || 'your coach'} and it'll count toward this pack.
          </div>
        )}
        {low && (
          <div style={{ fontSize: '12px', color: '#e67e22', fontWeight: 'bold', marginTop: '6px' }}>
            ⚠️ Only {p.remaining} session{p.remaining !== 1 ? 's' : ''} left — ask your coach about renewing.
          </div>
        )}
      </div>

      {Array.isArray(plan.bookingSlots) && plan.bookingSlots.length > 0 && (
        <>
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              marginTop: '10px', width: '100%', padding: '6px 10px', background: '#f8f9fa',
              border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
              fontWeight: 'bold', color: '#2c3e50', display: 'flex', justifyContent: 'space-between'
            }}
          >
            <span>Sessions ({plan.bookingSlots.length})</span>
            <span>{open ? '▼' : '▶'}</span>
          </button>
          {open && (
            <div style={{ marginTop: '6px' }}>
              {[...plan.bookingSlots]
                .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))
                .map(s => (
                  <SessionRow key={s.sequenceNumber} session={s} />
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function MyCoachingPacks() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await bookings.getMyBookedPlans();
      setPlans(res.data || []);
    } catch (err) {
      console.error('❌ Error loading coaching packs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || plans.length === 0) return null;

  const active = plans.filter(p => p.bookingStatus !== 'completed');
  const done = plans.filter(p => p.bookingStatus === 'completed');

  return (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{
        color: '#2c3e50',
        margin: '0 0 10px 0',
        fontSize: '14px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }}>
        🎟️ My Coaching Packs
      </h4>
      {active.map(plan => <PackCard key={plan._id} plan={plan} />)}
      {done.map(plan => <PackCard key={plan._id} plan={plan} />)}
    </div>
  );
}
